import { useEffect } from 'react'
import styled from 'styled-components/macro'

import { Layout } from '@loadsmart/loadsmart-ui'
import { getToken } from '@loadsmart/loadsmart-ui/dist/theming'

import { AsyncContent } from 'core/ui/async-content/AsyncContent'
import { LoadingState } from 'core/ui/async-content/LoadingState'
import { useAbortController } from 'core/ui/hooks/useAbortController'
import { MainContent } from 'core/ui/MainContent'

import { TokenExpiredError } from 'auth/errors/TokenExpiredError'
import { useRefreshInviteToken } from 'auth/ui/hooks/useRefreshInviteToken'

import { AwardedEmptyState } from './AwardedEmptyState'
import { AwardedProposalConfirmationHeader } from './AwardedProposalConfirmationHeader'
import { AwardedProposalConfirmationLanesList } from './AwardedProposalConfirmationLanesList'
import { useAwardedProposal } from './hooks/useAwardedProposal'
import { ProposalPageErrorState } from './ProposalPageErrorState'

const StyledLayoutStack = styled(Layout.Stack)`
  padding-top: ${getToken('space-2xl')};
`

type AwardedProposalConfirmationPageProps = {
  match: {
    params: {
      id: string
    }
  }
}

export function AwardedProposalConfirmationPage({
  match,
}: AwardedProposalConfirmationPageProps): JSX.Element {
  const { id } = match.params

  const { awardedProposal, awardedProposalStatus, error, fetchAwardedProposal } =
    useAwardedProposal()
  const { emailRecipient, refreshInviteToken, refreshInviteTokenStatus } = useRefreshInviteToken()
  const abortController = useAbortController([id])

  useEffect(() => {
    fetchAwardedProposal(id, abortController)
  }, [abortController, id, fetchAwardedProposal])

  useEffect(() => {
    if (error instanceof TokenExpiredError) {
      refreshInviteToken({ carrierInviteLinkUuid: id }, { signal: abortController.signal })
    }
  }, [abortController, id, error, refreshInviteToken])

  return (
    <MainContent>
      <AsyncContent
        status={awardedProposalStatus}
        loadingState={<LoadingState />}
        errorState={
          <ProposalPageErrorState
            error={error}
            emailRecipient={emailRecipient}
            refreshInviteTokenStatus={refreshInviteTokenStatus}
          />
        }
        emptyState={<AwardedEmptyState />}
        count={awardedProposal?.lanes.length}
      >
        {awardedProposal && (
          <StyledLayoutStack>
            <AwardedProposalConfirmationHeader awardedProposal={awardedProposal} />
            <AwardedProposalConfirmationLanesList rfpId={id} awardedProposal={awardedProposal} />
          </StyledLayoutStack>
        )}
      </AsyncContent>
    </MainContent>
  )
}
